import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ALL_PARAMETERS, ParameterKey } from '../../utils/weldParameterAggregator'
import { getWeldParameterHeaderLabel } from '../../utils/weldParameterHeaderLabels'
import {
  AggKey,
  defaultVisibleColumns,
  VisibleColumns
} from '../../utils/weldParametersColumnLayout'
import { getParameterUnitSuffix } from '../../utils/weldUnitUtils'
import { useUnitSetting } from '../../utils/userUnitSetting'
import ColumnFilterPopover from '../ui/ColumnFilterPopover'

/**
 * Filter target addressed by the optional `filterApi` prop. The matrix only
 * knows the *shape* of the target (pinned column vs metric sub-column); the
 * parent view supplies real options and applies filtering before passing the
 * filtered `passLabels` back in.
 */
export type FilterTarget =
  | { kind: 'pass' }
  | { kind: 'torch' }
  | { kind: 'metric'; col: ParameterKey; agg: AggKey }

export interface FilterApi {
  /** Stable list of unique values for one filter target, in display order. */
  getOptions: (target: FilterTarget) => string[]
  /** Current selection (`null` = no filter active). */
  getValue: (target: FilterTarget) => string[] | null
  /** Optional contains/equals text filter the popover surfaces. */
  getText?: (target: FilterTarget) => string
  onChange: (target: FilterTarget, value: string[] | null) => void
  onTextChange?: (target: FilterTarget, text: string) => void
}

export interface DisplayColumn {
  key: ParameterKey
  label: string
}

/** Parameter keys in display order; header labels are computed at render time. */
export const DISPLAY_COLUMNS: DisplayColumn[] = ALL_PARAMETERS.map((key) => ({
  key,
  label: key
}))

if (DISPLAY_COLUMNS.length !== ALL_PARAMETERS.length) {
  // eslint-disable-next-line no-console
  console.warn('ParameterMatrix: DISPLAY_COLUMNS is out of sync with ALL_PARAMETERS')
}

export const AGG_KEYS: AggKey[] = ['min', 'avg', 'max']
const AGG_LABEL: Record<AggKey, string> = { min: 'Min', avg: 'Avg', max: 'Max' }

const SELECT_COL_W = 44
const PASS_COL_W = 128
const ACTIONS_COL_W = 64
const TORCH_COL_W = 120
const MIN_SUB_W = 100

// Chrome primitives mirror WeldPassDetailTable.tsx (the floorsheet reference):
//   - bg-gray-50 sticky header, h-[72px] split across the two header rows
//   - h-[52px] body rows on white with hover-gray-50
//   - sticky-chain shadow on every frozen leftmost cell so they read as a
//     single chained column when the table scrolls horizontally
//   - border-l between metric groups, lighter border-l between Min/Avg/Max
//     sub-cells inside the same group
//   - bottom borders live on cells (not on `<tr>`) because the table uses
//     `border-collapse: separate`, which makes `border-b` on a `<tr>` invisible
const STICKY_CHAIN_SHADOW = 'shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
const STICKY_HEADER_CHAIN_CLASS = `sticky z-30 bg-gray-50 ${STICKY_CHAIN_SHADOW}`
const STICKY_BODY_CHAIN_CLASS = `sticky z-10 bg-white ${STICKY_CHAIN_SHADOW}`
const HEADER_CELL_CLASS = 'px-4 align-middle text-xs font-medium text-gray-600'
const BODY_CELL_CLASS = 'px-4 align-middle text-xs text-gray-600'
const HEADER_BOTTOM_BORDER = 'border-b border-[#E2E5EA]'
const BODY_BOTTOM_BORDER = 'border-b border-gray-200'
const METRIC_DIVIDER_HEADER = 'border-l border-[#E2E5EA]'
const METRIC_DIVIDER_BODY = 'border-l border-[#EEF0F3]'

interface StickyEntry {
  key: string
  width: number
  sticky: boolean
}

/**
 * Mirrors WeldPassDetailTable.tsx's `getStickyLeft`: walks the leading sticky
 * columns in declaration order and returns the cumulative `left` offset for
 * each. Hidden sticky columns are skipped so the remaining ones still chain
 * cleanly.
 */
function buildStickyLeftMap(items: StickyEntry[]): Map<string, number> {
  const map = new Map<string, number>()
  let offset = 0
  for (const item of items) {
    if (!item.sticky) continue
    map.set(item.key, offset)
    offset += item.width
  }
  return map
}

export interface RenderableColumn extends DisplayColumn {
  aggs: AggKey[]
}

export interface ParameterMatrixProps {
  passLabels: string[]
  visibleColumns?: VisibleColumns
  renderTorchCell: (pass: string) => React.ReactNode
  renderValueCell: (pass: string, col: DisplayColumn, agg: AggKey) => React.ReactNode
  /** Optional Pass cell content; defaults to the pass label string. */
  renderPassCell?: (pass: string) => React.ReactNode
  /**
   * Optional row-actions renderer. When provided, an "Actions" column is
   * rendered immediately after the sticky Pass column (and before the Torch /
   * parameter columns). The returned node is typically a small icon button
   * (see Pass Analytics eye icon).
   */
  renderRowActions?: (pass: string) => React.ReactNode
  /**
   * Whether the leading checkbox-selection column is rendered. Defaults to
   * `true`. When enabled, the table starts with every pass row selected and
   * the header checkbox toggles select-all / clear-all.
   */
  selectable?: boolean
  /**
   * Optional Excel-style filter API. When provided, an inline funnel button
   * is rendered next to the Pass / Torch column labels and next to every
   * Min/Avg/Max sub-header. The matrix never reads row data itself — the
   * parent view supplies options/values/onChange and is responsible for
   * applying filters to `passLabels` before passing them in.
   */
  filterApi?: FilterApi
  /** Unit suffix for the Preheat / Interpass column (from pipe `preheatUnit`). */
  preheatUnit?: string
  testId?: string
}

/**
 * Shared table chrome for the Floorsheet Welding Parameters page.
 *
 * Layout pinned to the WeldPassDetailTable.tsx reference: rounded card,
 * subtle border, sticky two-row header (parameter group + Min/Avg/Max),
 * sticky-frozen Select / Pass / Actions / Torch columns, h-[72px] header
 * and h-[52px] body rows with the same sticky-chain shadow used by
 * PassAnalyticsTable.
 *
 * Cell content (torch pill vs dropdown, read-only value vs input) is
 * supplied by `renderTorchCell` / `renderValueCell` so this layout can be
 * shared by the uploaded (read-only) and manual (editable) views.
 *
 * Column order:
 *   1. Select (checkbox, sticky leftmost)            — when `selectable`
 *   2. Pass                                          — sticky
 *   3. Actions                                       — when `renderRowActions`
 *   4. Torch                                         — when `visibleColumns.torch`
 *   5. Parameter (Min / Avg / Max) groups
 */
export default function ParameterMatrix({
  passLabels,
  visibleColumns,
  renderTorchCell,
  renderValueCell,
  renderPassCell,
  renderRowActions,
  selectable = true,
  filterApi,
  preheatUnit,
  testId
}: ParameterMatrixProps) {
  useUnitSetting()
  const unitProbe = getParameterUnitSuffix('travelSpeed')
  const renderFunnel = (target: FilterTarget, testIdSuffix: string): React.ReactNode => {
    if (!filterApi) return null
    const options = filterApi.getOptions(target)
    if (options.length === 0) return null
    return (
      <ColumnFilterPopover
        testId={`wp-filter-${testIdSuffix}`}
        options={options}
        value={filterApi.getValue(target)}
        textFilter={filterApi.getText?.(target) ?? ''}
        onChange={(values) => filterApi.onChange(target, values)}
        onTextFilterChange={
          filterApi.onTextChange
            ? (text) => filterApi.onTextChange!(target, text)
            : undefined
        }
      />
    )
  }
  const visibility = visibleColumns ?? defaultVisibleColumns()
  const torchVisible = Boolean(visibility.torch)
  const actionsVisible = Boolean(renderRowActions)

  // Sticky-left chain — drives the cumulative `left` offsets so all four
  // sticky columns chain cleanly when the table scrolls horizontally.
  const stickyLeft = useMemo(
    () =>
      buildStickyLeftMap([
        { key: 'select', width: SELECT_COL_W, sticky: selectable },
        { key: 'pass', width: PASS_COL_W, sticky: true },
        { key: 'actions', width: ACTIONS_COL_W, sticky: actionsVisible },
        { key: 'torch', width: TORCH_COL_W, sticky: torchVisible }
      ]),
    [selectable, actionsVisible, torchVisible]
  )

  const selectLeft = stickyLeft.get('select') ?? 0
  const passLeft = stickyLeft.get('pass') ?? 0
  const actionsLeft = stickyLeft.get('actions') ?? 0
  const torchLeft = stickyLeft.get('torch') ?? 0

  const [selectedPasses, setSelectedPasses] = useState<Set<string>>(
    () => new Set(passLabels)
  )
  // Tracks every pass label this component has rendered for. Used to
  // distinguish a *brand-new* pass (default-selected) from a pass the user
  // has explicitly deselected (must remain deselected even on re-render).
  const seenPassesRef = useRef<Set<string>>(new Set(passLabels))

  useEffect(() => {
    setSelectedPasses((prev) => {
      let changed = false
      const next = new Set<string>()
      for (const pass of passLabels) {
        if (prev.has(pass)) {
          next.add(pass)
        } else if (!seenPassesRef.current.has(pass)) {
          next.add(pass)
          changed = true
        }
        seenPassesRef.current.add(pass)
      }
      if (next.size !== prev.size) changed = true
      return changed ? next : prev
    })
  }, [passLabels])

  const totalSelectable = passLabels.length
  const selectedCount = selectedPasses.size
  const allSelected = totalSelectable > 0 && selectedCount === totalSelectable
  const someSelected = selectedCount > 0 && selectedCount < totalSelectable

  const togglePass = (pass: string) => {
    setSelectedPasses((prev) => {
      const next = new Set(prev)
      if (next.has(pass)) next.delete(pass)
      else next.add(pass)
      return next
    })
  }

  const toggleAll = () => {
    setSelectedPasses(allSelected ? new Set() : new Set(passLabels))
  }

  const renderableColumns: RenderableColumn[] = useMemo(
    () =>
      DISPLAY_COLUMNS.map((col) => {
        const flags = visibility[col.key] ?? { min: false, avg: false, max: false }
        const aggs = AGG_KEYS.filter((k) => flags[k])
        return {
          ...col,
          label: getWeldParameterHeaderLabel(col.key, preheatUnit),
          aggs
        }
      }).filter((col) => col.aggs.length > 0),
    [visibility, unitProbe, preheatUnit]
  )

  return (
    <div
      data-testid={testId}
      className="w-full max-w-full overflow-hidden rounded-lg border border-[#E2E5EA] bg-white"
    >
      <div
        className="w-full max-w-full overflow-x-auto overflow-y-auto"
        style={{
          maxHeight: 'calc(100vh - 240px)',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain'
        }}
      >
        <table
          className="border-collapse"
          style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}
        >
          <thead className="sticky top-0 z-20 bg-gray-50">
            {/* Top header row — group labels + sticky frozen columns. */}
            <tr className="h-10 bg-gray-50">
              {selectable ? (
                <th
                  rowSpan={2}
                  scope="col"
                  data-testid="wp-select-header"
                  className={`${HEADER_CELL_CLASS} ${STICKY_HEADER_CHAIN_CLASS} ${HEADER_BOTTOM_BORDER} h-[72px] text-center`}
                  style={{
                    left: selectLeft,
                    minWidth: SELECT_COL_W,
                    width: SELECT_COL_W
                  }}
                >
                  <span className="flex h-full w-full items-center justify-center">
                    <input
                      type="checkbox"
                      data-testid="wp-select-all"
                      aria-label={
                        allSelected ? 'Deselect all passes' : 'Select all passes'
                      }
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected
                      }}
                      checked={allSelected}
                      onChange={toggleAll}
                      className="h-3.5 w-3.5 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                    />
                  </span>
                </th>
              ) : null}
              <th
                rowSpan={2}
                scope="col"
                className={`${HEADER_CELL_CLASS} ${STICKY_HEADER_CHAIN_CLASS} ${HEADER_BOTTOM_BORDER} h-[72px] text-center`}
                style={{
                  left: passLeft,
                  minWidth: PASS_COL_W
                }}
              >
                <span className="inline-flex items-center justify-center gap-0.5">
                  <span>Pass</span>
                  {/* {renderFunnel({ kind: 'pass' }, 'pass')} */}
                </span>
              </th>
              {actionsVisible ? (
                <th
                  rowSpan={2}
                  scope="col"
                  data-testid="wp-actions-header"
                  className={`${HEADER_CELL_CLASS} ${STICKY_HEADER_CHAIN_CLASS} ${HEADER_BOTTOM_BORDER} h-[72px] text-center`}
                  style={{
                    left: actionsLeft,
                    minWidth: ACTIONS_COL_W,
                    width: ACTIONS_COL_W
                  }}
                >
                  Actions
                </th>
              ) : null}
              {torchVisible ? (
                <th
                  rowSpan={2}
                  scope="col"
                  className={`${HEADER_CELL_CLASS} ${STICKY_HEADER_CHAIN_CLASS} ${HEADER_BOTTOM_BORDER} h-[72px] text-center`}
                  style={{
                    left: torchLeft,
                    minWidth: TORCH_COL_W
                  }}
                >
                  <span className="inline-flex items-center justify-center gap-0.5">
                    <span>Torch</span>
                    {renderFunnel({ kind: 'torch' }, 'torch')}
                  </span>
                </th>
              ) : null}
              {renderableColumns.map((col, idx) => (
                <th
                  key={col.key}
                  scope="colgroup"
                  colSpan={col.aggs.length}
                  className={`${HEADER_CELL_CLASS} text-center ${
                    idx > 0 ? METRIC_DIVIDER_HEADER : ''
                  }`}
                  style={{ minWidth: col.aggs.length * MIN_SUB_W }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
            {/* Sub-header row — Min / Avg / Max under each parameter group.
                Uses the same chrome typography as the WeldPassDetailTable
                stats grid (text-xs font-normal text-gray-500) so the two
                rows read together as a single header block. The bottom
                border lives on each `<th>` so it actually paints under the
                separate-collapse table model. */}
            <tr className="h-8 bg-gray-50">
              {renderableColumns.map((col, idx) => (
                <React.Fragment key={`mam-${col.key}`}>
                  {col.aggs.map((agg, aIdx) => (
                    <th
                      key={agg}
                      scope="col"
                      className={`${HEADER_BOTTOM_BORDER} px-4 pb-2 text-center text-xs font-normal text-gray-500 ${
                        aIdx === 0 && idx > 0 ? METRIC_DIVIDER_HEADER : ''
                      } ${aIdx > 0 ? METRIC_DIVIDER_BODY : ''}`}
                      style={{ minWidth: MIN_SUB_W }}
                    >
                      <span className="inline-flex items-center justify-center gap-0.5">
                        <span>{AGG_LABEL[agg]}</span>
                        {renderFunnel(
                          { kind: 'metric', col: col.key, agg },
                          `${col.key}-${agg}`
                        )}
                      </span>
                    </th>
                  ))}
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {passLabels.map((pass) => {
              const isSelected = selectedPasses.has(pass)
              const dimClass = isSelected ? '' : 'opacity-60'
              return (
                <tr
                  key={pass}
                  data-testid={`wp-row-${pass}`}
                  data-selected={isSelected ? 'true' : 'false'}
                  className={`group h-[52px] bg-white transition-colors hover:bg-gray-50 ${dimClass}`.trim()}
                >
                  {selectable ? (
                    <td
                      data-testid={`wp-select-${pass}`}
                      className={`${BODY_CELL_CLASS} ${STICKY_BODY_CHAIN_CLASS} ${BODY_BOTTOM_BORDER} text-center`}
                      style={{
                        left: selectLeft,
                        minWidth: SELECT_COL_W,
                        width: SELECT_COL_W
                      }}
                    >
                      <span className="flex h-full w-full items-center justify-center">
                        <input
                          type="checkbox"
                          data-testid={`wp-select-checkbox-${pass}`}
                          aria-label={`Select pass ${pass}`}
                          checked={isSelected}
                          onChange={() => togglePass(pass)}
                          className="h-3.5 w-3.5 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                        />
                      </span>
                    </td>
                  ) : null}
                  <td
                    className={`${BODY_CELL_CLASS} ${STICKY_BODY_CHAIN_CLASS} ${BODY_BOTTOM_BORDER} relative whitespace-nowrap text-center font-semibold text-text-grey`}
                    style={{
                      left: passLeft,
                      minWidth: PASS_COL_W,
                      width: PASS_COL_W
                    }}
                  >
                    {renderPassCell ? renderPassCell(pass) : pass}
                  </td>
                  {actionsVisible ? (
                    <td
                      className={`${BODY_CELL_CLASS} ${STICKY_BODY_CHAIN_CLASS} ${BODY_BOTTOM_BORDER} whitespace-nowrap text-center`}
                      style={{
                        left: actionsLeft,
                        minWidth: ACTIONS_COL_W,
                        width: ACTIONS_COL_W
                      }}
                      data-testid={`wp-actions-${pass}`}
                    >
                      <span className="flex h-full w-full items-center justify-center">
                        {renderRowActions!(pass)}
                      </span>
                    </td>
                  ) : null}
                  {torchVisible ? (
                    <td
                      className={`${BODY_CELL_CLASS} ${STICKY_BODY_CHAIN_CLASS} ${BODY_BOTTOM_BORDER} whitespace-nowrap text-center`}
                      style={{
                        left: torchLeft,
                        minWidth: TORCH_COL_W
                      }}
                      data-testid={`wp-cell-${pass}-torch`}
                    >
                      {renderTorchCell(pass)}
                    </td>
                  ) : null}
                  {renderableColumns.map((col, idx) => (
                    <React.Fragment key={`${pass}-${col.key}`}>
                      {col.aggs.map((agg, aIdx) => (
                        <td
                          key={agg}
                          data-focus-field-cell={
                            aIdx === 0 ? `${col.key}:${pass}` : undefined
                          }
                          className={`${BODY_CELL_CLASS} ${BODY_BOTTOM_BORDER} text-center tabular-nums ${
                            aIdx === 0 && idx > 0 ? METRIC_DIVIDER_HEADER : ''
                          } ${aIdx > 0 ? METRIC_DIVIDER_BODY : ''}`}
                          style={{ minWidth: MIN_SUB_W }}
                        >
                          {renderValueCell(pass, col, agg)}
                        </td>
                      ))}
                    </React.Fragment>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
